import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Component, computed, ElementRef, inject, input, NgZone, Renderer2, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { EnpsComponent } from '@common/components/base/enps-component';
import { AttachmentListItem } from '@common/data-models/complex-types/attachment-list';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { LoaderComponent } from '@loader/loader';
import { Store } from '@ngrx/store';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { AppService } from '@suite/app.service';
import { AttachmentService } from '@suite/common/services/attachment-service';
import { BasicContentService } from '@suite/common/services/basic-content-service';
import { UploaderOptions, UploadFile, UploadOutput } from 'ngx-uploader';

declare let window;

@Component({
    selector: 'st-attachments-list',
    templateUrl: './attachments-list.html',
    styleUrls: ['./attachments-list.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-attachments-list',
    },
})
export class AttachmentsListComponent extends EnpsComponent {
    store = inject(Store);
    appService = inject(AppService);
    ngZone = inject(NgZone);
    renderer = inject(Renderer2);
    nomWebServicesJson = inject(NomWebServicesJson);
    visualStateService = inject(VisualStateService);
    globalTablesService = inject(GlobalTablesService);
    basicContentService = inject(BasicContentService);
    attachmentService = inject(AttachmentService);
    changeDetectorRef = inject(ChangeDetectorRef);
    modalService = inject(ModalService);

    // inputs
    record = input<StorytellingRecord>({});
    loader = input<LoaderComponent>();
    disabled = input(false, {
        transform: coerceBooleanProperty,
    });

    // store signals
    languageSettings = this.store.selectSignal(selectI18N)();

    // signals
    recordAttachments = signal<Array<UploadFile>>([]);
    options = signal<UploaderOptions>({
        concurrency: 1,
        maxFileSize: this.attachmentService.maxExternalDocumentSize,
    });

    isAndroidWrapper = signal<boolean>(null);
    dragOver = signal<boolean>(null);
    editable = signal<boolean>(null);
    private unbindNAUListener: () => void = null;

    input = viewChild<ElementRef<HTMLInputElement>>('file');

    linkedRecords = computed(() => {
        return this.record()?.LinkedRecords || [];
    });

    constructor() {
        super();
        this.isAndroidWrapper.set(this.visualStateService.checkIfAdroidWrapper());
        this.editable.set(this.globalTablesService.securityPrivileges[this.nomWebServicesJson.PSTGroup].topic.write);
    }

    async removeAttachment(attachment: AttachmentListItem) {
        this.store.dispatch(RecordHttpActions.removeAttachment({ attachment, record: this.record() }));
    }

    browse(): void {
        if (this.record()?.isNewRecord) {
            this.showWarningMessage();
        } else {
            this.input().nativeElement.click();
        }
    }

    onUploadOutput(output: UploadOutput) {
        const uploadFile = () => {
            if (this.record()?.isNewRecord) {
                this.showWarningMessage();
            } else {
                this.store.dispatch(RecordHttpActions.shouldProceedAttachment({ recordAttachments: this.recordAttachments(), record: this.record() }));
                this.recordAttachments.set([]);
            }
        };
        switch (output.type) {
            case 'allAddedToQueue':
                uploadFile();
                break;
            case 'addedToQueue':
                if (typeof output.file !== 'undefined') {
                    this.recordAttachments.update(value => {
                        return [...(value || []), output.file];
                    });
                }
                break;
            case 'dragOver':
                this.dragOver.set(true);
                break;
            case 'dragOut':
            case 'drop':
                this.dragOver.set(false);
                break;
        }
        this.input().nativeElement.value = '';
    }

    showWarningMessage() {
        this.modalService.openAlert(this.languageSettings[i18nKeys.PSTWebNewRecordFileUploadWarningTitle], this.languageSettings[i18nKeys.PSTWebNewRecordFileUploadWarningContent]);
    }

    async nativeAndroidUpload(event) {
        if (this.unbindNAUListener) this.unbindNAUListener();
        this.store.dispatch(RecordHttpActions.nativeAndroidUpload({ nativeAndroidEvent: event, record: this.record() }));
    }

    fireUploadEvent(event: Event) {
        if (this.isAndroidWrapper) {
            window.globalAttachmentForm = null;
            if (this.input() && this.input().nativeElement) {
                window.globalAttachmentForm = this.input().nativeElement;
            }

            if (window.globalAttachmentForm && window.ANDROID_WRAPPER && window.ANDROID_WRAPPER.openFileChooser) {
                this.ngZone.runOutsideAngular(() => {
                    if (this.input()) {
                        if (this.unbindNAUListener) this.unbindNAUListener();
                        this.unbindNAUListener = this.renderer.listen(this.input().nativeElement, 'nativeAndroidFileUpload', this.nativeAndroidUpload.bind(this));
                    }
                });
                window.ANDROID_WRAPPER.openFileChooser();
            }
            event.preventDefault();
            event.stopPropagation();
            return false;
        }
    }

    async downloadAttachment(attachment: AttachmentListItem) {
        if (window.ANDROID_WRAPPER) {
            this.loader().show();
            this.loader().label = this.languageSettings.PSTWebDownloading;
        }
        await this.appService.attachmentService.downloadAttachment(attachment.database, attachment.path, attachment.guid, attachment.title);
        if (window.ANDROID_WRAPPER) {
            this.loader().hide();
            this.loader().label = '';
        }
    }
}
